/**
 * Hugging Face OAuth helper for the Med-Curation landing / callback pages.
 *
 * This mirrors the LabelBricks web flow:
 *   1. POST {apiBase}/api/auth/huggingface/start  { redirectUri } -> { authorizationUrl }
 *   2. Browser is redirected to authorizationUrl (Hugging Face consent screen).
 *   3. Hugging Face redirects back to redirectUri with ?code=...&state=...
 *   4. POST {apiBase}/api/auth/huggingface/complete { code, state, redirectUri } -> { token }
 *   5. The LabelBricks session token is persisted for downstream API calls.
 *
 * The LabelBricks backend accepts the redirectUri supplied here and uses it to
 * build the Hugging Face authorization URL, so the redirectUri below is the value
 * that must be registered as an allowed callback in the Hugging Face OAuth app.
 */

/** Path (within this app) that Hugging Face redirects back to. */
export const HUGGING_FACE_CALLBACK_PATH = '/auth/huggingface/callback';

/** localStorage key used to persist the LabelBricks session token. */
export const LABELBRICKS_TOKEN_KEY = 'token';

/** localStorage key remembering where to return after a successful sign-in. */
export const HUGGING_FACE_RETURN_PATH_KEY = 'medcuration_hf_return_path';

const DEFAULT_LABELBRICKS_API_BASE = 'https://api.labelbricks.com';
const DEFAULT_LABELBRICKS_APP_URL = 'https://labelbricks.com';

/**
 * Resolve the LabelBricks API origin (without trailing slash).
 * Configurable via `window.config.labelBricksApiUrl` in the runtime config so
 * deployments can point it at the correct backend without a rebuild.
 */
export function getLabelBricksApiBase(): string {
  const runtimeConfig =
    (typeof window !== 'undefined' && (window as any).config) || {};

  const configured =
    runtimeConfig.labelBricksApiUrl ||
    runtimeConfig.labelbricksApiUrl ||
    DEFAULT_LABELBRICKS_API_BASE;

  return String(configured).replace(/\/$/, '');
}

export function getLabelBricksAppUrl(): string {
  const runtimeConfig =
    (typeof window !== 'undefined' && (window as any).config) || {};

  const configured =
    runtimeConfig.labelBricksAppUrl ||
    runtimeConfig.labelbricksAppUrl ||
    DEFAULT_LABELBRICKS_APP_URL;

  return String(configured).replace(/\/$/, '');
}

/** Absolute redirect URI that Hugging Face will call back. */
export function getHuggingFaceRedirectUri(): string {
  return `${window.location.origin}${HUGGING_FACE_CALLBACK_PATH}`;
}

const START_ENDPOINTS = [
  '/api/auth/huggingface/start',
  '/api/integrations/huggingface/start',
];

const COMPLETE_ENDPOINTS = [
  '/api/auth/huggingface/complete',
  '/api/integrations/huggingface/complete',
];

export type HuggingFaceAuthStartResponse = {
  authorizationUrl: string;
  code?: string;
  message?: string;
};

export type HuggingFaceAuthCompleteResponse = {
  token?: string;
  code?: string;
  message?: string;
  huggingFaceUsername?: string;
  huggingFaceEmail?: string;
  labelBricksEmail?: string;
};

export type LabelBricksServerAccessStatus = {
  canUsePlatform: boolean;
  hasServer: boolean;
  serverCount: number;
  status: 'READY' | 'BLOCKED';
  message?: string;
  labelBricksUserId?: string;
  labelBricksEmail?: string;
  huggingFaceEmail?: string;
  server?: {
    id: string;
    name: string;
    providerLabel?: string | null;
    ipAddress: string;
    sshUser: string;
    sshPort: string;
    createdAt: string;
    updatedAt: string;
    hasHetznerApiToken: boolean;
  } | null;
};

async function readJsonSafe(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function requestWithFallback<T>(
  endpoints: string[],
  init: RequestInit,
  fallbackMessage: string
): Promise<T> {
  const apiBase = getLabelBricksApiBase();
  let lastMessage = fallbackMessage;

  for (const endpoint of endpoints) {
    let response: Response;
    try {
      response = await fetch(`${apiBase}${endpoint}`, {
        ...init,
      });
    } catch {
      lastMessage =
        'Unable to reach the LabelBricks authentication service. Check that the API is online and reachable from this origin.';
      continue;
    }

    if (response.ok) {
      return (await readJsonSafe(response)) as T;
    }

    // Try the next candidate only for "not implemented" style responses.
    if (response.status !== 404 && response.status !== 405) {
      const parsed = await readJsonSafe(response);
      throw new Error(parsed?.message || parsed?.error || fallbackMessage);
    }

    const parsed = await readJsonSafe(response);
    lastMessage = parsed?.message || lastMessage;
  }

  throw new Error(lastMessage);
}

export function startHuggingFaceAuth(
  redirectUri: string
): Promise<HuggingFaceAuthStartResponse> {
  return requestWithFallback<HuggingFaceAuthStartResponse>(
    START_ENDPOINTS,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ redirectUri }),
    },
    'Failed to start Hugging Face sign-in.'
  );
}

export function completeHuggingFaceAuth(payload: {
  code: string;
  state?: string;
  redirectUri: string;
}): Promise<HuggingFaceAuthCompleteResponse> {
  return requestWithFallback<HuggingFaceAuthCompleteResponse>(
    COMPLETE_ENDPOINTS,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    'Failed to complete Hugging Face sign-in.'
  );
}

/** Persist the LabelBricks session token for downstream API calls. */
export function persistLabelBricksToken(token: string): void {
  try {
    localStorage.setItem(LABELBRICKS_TOKEN_KEY, token);
  } catch {
    /* ignore storage failures */
  }
}

export function getStoredLabelBricksToken(): string | null {
  try {
    return localStorage.getItem(LABELBRICKS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearStoredLabelBricksToken(): void {
  try {
    localStorage.removeItem(LABELBRICKS_TOKEN_KEY);
  } catch {
    /* ignore storage failures */
  }
}

export function configureLabelBricksAuthSession(
  token: string,
  userAuthenticationService: any,
  handleUnauthenticated?: () => void
): void {
  userAuthenticationService.set({ enabled: true });
  userAuthenticationService.setUser({ access_token: token });
  userAuthenticationService.setServiceImplementation({
    getAuthorizationHeader: () => ({
      Authorization: `Bearer ${token}`,
    }),
    handleUnauthenticated: () => {
      handleUnauthenticated?.();
      return null;
    },
  });
}

export function resetLabelBricksAuthSession(
  userAuthenticationService: any
): void {
  userAuthenticationService.setUser(null);
  userAuthenticationService.set({ enabled: false });
  userAuthenticationService.setServiceImplementation({
    getAuthorizationHeader: () => undefined,
    handleUnauthenticated: () => null,
  });
}

const ACCESS_STATUS_ENDPOINTS = [
  '/api/servers/access-status/self',
  '/api/integrations/labelbricks/access-status/self',
];

export function getLabelBricksServerAccessStatus(
  token: string
): Promise<LabelBricksServerAccessStatus> {
  return requestWithFallback<LabelBricksServerAccessStatus>(
    ACCESS_STATUS_ENDPOINTS,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
    'Failed to verify server access for this LabelBricks account.'
  );
}
